// Форматтеры представления. Чистые функции: вход — доменные значения (ISO-timestamp из
// shared-contracts, размер в байтах, имя), выход — строка для UI. Локализация — к часовому поясу
// клиента (Intl). Все timestamps в домене — ISO-8601 UTC (SPEC §4.4).
/** Время в пузыре «ЧЧ:ММ» по локали/таймзоне клиента. */
export function formatTime(iso, now = new Date()) {
    void now;
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(d);
}
/** Совпадают ли две даты по календарному дню (в локальной таймзоне). */
function sameLocalDay(a, b) {
    return (a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate());
}
/**
 * Относительная подпись разделителя дат: «Сегодня» / «Вчера» / «12 июня» (в текущем году) /
 * «12 июня 2024» (в прошлом). Локаль — клиента (Intl). FR-MSG-13.
 */
export function formatDateSeparator(iso, now = new Date()) {
    const d = new Date(iso);
    if (sameLocalDay(d, now))
        return 'Сегодня';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameLocalDay(d, yesterday))
        return 'Вчера';
    const sameYear = d.getFullYear() === now.getFullYear();
    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'long',
        year: sameYear ? undefined : 'numeric',
    }).format(d);
}
/** «Время последнего сообщения» в строке списка: время сегодня, «Вчера», иначе дата. */
export function formatListTimestamp(iso, now = new Date()) {
    const d = new Date(iso);
    if (sameLocalDay(d, now))
        return formatTime(iso, now);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameLocalDay(d, yesterday))
        return 'Вчера';
    const sameYear = d.getFullYear() === now.getFullYear();
    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: sameYear ? undefined : '2-digit',
    }).format(d);
}
/** «был(а) в сети …» относительно now. */
export function formatLastSeen(iso, now = new Date()) {
    const d = new Date(iso);
    const diffMs = now.getTime() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1)
        return 'был(а) в сети только что';
    if (min < 60)
        return `был(а) в сети ${min} мин назад`;
    const hours = Math.floor(min / 60);
    if (sameLocalDay(d, now))
        return `был(а) в сети в ${formatTime(iso, now)}`;
    if (hours < 48)
        return 'был(а) в сети вчера';
    return `был(а) в сети ${formatDateSeparator(iso, now)}`;
}
/** Длительность аудио/голосового «М:СС» или «Ч:ММ:СС». */
export function formatDuration(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    const s = total % 60;
    const m = Math.floor(total / 60) % 60;
    const h = Math.floor(total / 3600);
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
/** Человекочитаемый размер файла (FR-MEDIA-04). */
export function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} Б`;
    const units = ['КБ', 'МБ', 'ГБ', 'ТБ'];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i++;
    }
    const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} ${units[i]}`;
}
/**
 * Подпись прогресса загрузки (DLP-5): «45% · 1.2 МБ из 3.4 МБ»; всего неизвестно — «1.2 МБ».
 */
export function formatTransferProgress(loadedBytes, totalBytes) {
    if (totalBytes === undefined || totalBytes <= 0)
        return formatFileSize(loadedBytes);
    const pct = Math.min(100, Math.max(0, Math.round((loadedBytes / totalBytes) * 100)));
    return `${pct}% · ${formatFileSize(Math.min(loadedBytes, totalBytes))} из ${formatFileSize(totalBytes)}`;
}
/** Иконка-подпись превью по kind не-текстового сообщения (SPEC §4.3 ChatListItem). */
export function previewForKind(kind, text) {
    switch (kind) {
        case 'media':
            return text && text.length > 0 ? `🖼 ${text}` : '🖼 Фото';
        case 'voice':
            return '🎤 Голосовое';
        case 'system':
            return text ?? 'Системное сообщение';
        case 'text':
        default:
            return text ?? '';
    }
}
/**
 * Инициалы из имени (1–2 буквы) для Avatar-fallback. Берёт первые буквы первых двух слов;
 * для одного слова — одну букву. Защита от пустого/пробельного имени.
 */
export function initialsOf(name) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return '?';
    if (words.length === 1)
        return (words[0]?.[0] ?? '?').toUpperCase();
    const first = words[0]?.[0] ?? '';
    const second = words[1]?.[0] ?? '';
    return (first + second).toUpperCase();
}
//# sourceMappingURL=format.js.map